import React, { useCallback, useState } from "react";
import PropTypes from "prop-types";
import GoodsamMembershipModal from "./GoodsamMembershipModal";
import { useMembershipOffers } from "components/GoodsamMembership/query";
import { MembershipValidationModal } from "components/MembershipValidationModal";
import { useLazyQuery } from "@apollo/client";
import { userPhoneVerifyQuery } from "components/MembershipValidationModal/queries";
import { membershipValidate } from "components/MembershipValidationModal/mutations";
import {
  extractSuccess,
  extractServerErrorMessage
} from "utils/extractErrors";
import { useMutation } from "@apollo/client";
import NotificationPopover from "components/NotificationPopover/NotificationPopover";
import { SpinnerBack } from "components/Spinner/Spinner";
import { ModalMembershipValidation } from "components/ModalMembershipValidation/ModalMembershipValidation";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { MEMBERSHIP_VALIDATION_MODES } from "constants/membershipValidationModes";

const extractMembershipValidateSuccess = extractSuccess("membership_validate");
const extractMembershipValidateErrorMessage = extractServerErrorMessage("membership_validate");

const contentData = {
  membershipValidateText: "Already a Member?",
  membershipValidateCtaLabel: "Validate Membership",
  membershipDisclaimer: `
    <p>
      *Offers are not retroactive.
      Sales tax on memberships is applicable in certain states.
    </p>
    <p>
      Good Sam membership benefits, promotions and/or special
      offers are subject to change without prior notice. Some
      benefits, products and services offered by Good Sam may not
      be available to residents in certain states or outside the
      U.S. Some restrictions apply to certain benefits. Membership
      fees are non-refundable.
    </p>
  `,
  failedValidationMessageTitle: "Membership not validated",
  failedValidationMessageText:
    "The information you provided is not associated with an active Good Sam Membership. Please review and submit again, or contact us.",
  tryAgainButton: {
    label: "Try Again"
  },
  contactButton: {
    link: "https://www.goodsam.com/contact/",
    label: "Contact Good Sam"
  },
  cancelButton: {
    label: "Cancel"
  },
  confirmButton: {
    label: "Confirm"
  }
};

const GoodsamMembership = (props) => {
  const { isFeatureFlagEnabled: isAgisMembershipMode } = useFeatureFlags(MEMBERSHIP_VALIDATION_MODES.AGIS);
  const {
    activeId,
    show,
    clubId = "goodsam",
    onClose,
    setIsGoodSamMember,
    setIsDeclined,
    backTo,
    onSavePlan,
    setIsShowMembershipModal,
    onMembershipValidationSuccess,
    ...restProps
  } = props;
  const [error, setError] = useState("");
  const [inVerifying, setInVerifying] = useState(false);
  const [membershipValidateAction, { loading: membershipValidateLoading }] = useMutation(membershipValidate, {
    fetchPolicy: "no-cache",
    onCompleted: (response) => {
      if(extractMembershipValidateSuccess({ data: response })) {
        onMembershipValidationSuccess();
        onClose();
        setInVerifying(false);
      } else {
        const errorText = extractMembershipValidateErrorMessage({ data: response });
        setError(errorText);
      }
    }
  });

  const [userPhoneVerifyAction, { loading: userPhoneVerifyLoading }] = useLazyQuery(userPhoneVerifyQuery, {
    fetchPolicy: "no-cache",
    onCompleted: (response) => {
      if (response?.user?.phone_verified) {
        membershipValidateAction({
          variables: {
            club_id: clubId,
            phone: response?.user?.phone
          }
        });
      } else {
        setInVerifying(true);
      }
    }
  });

  const onVerify = useCallback(() => {
    userPhoneVerifyAction();
  }, [userPhoneVerifyAction]);

  const onMembershipValidationModalClose = useCallback(() => {
    setInVerifying(false);
  }, [setInVerifying]);

  const { loading, data } = useMembershipOffers();

  if (loading) return null;

  return (
    <>
      {(userPhoneVerifyLoading || membershipValidateLoading) && <SpinnerBack />}
      <GoodsamMembershipModal
        activeId={activeId}
        content={contentData}
        loading={loading}
        options={data?.good_sam_membership_offers || []}
        show={show}
        onClose={onClose}
        backTo={backTo}
        toggleVerify={onVerify}
        onSavePlan={onSavePlan}
        errorText={error}
        setError={setError}
        {...restProps}
      />

      {clubId && inVerifying && !isAgisMembershipMode ? (
        <ModalMembershipValidation
          clubId={clubId}
          show={inVerifying}
          onClose={onMembershipValidationModalClose}
          onMembershipValidationSuccess={onMembershipValidationSuccess}
        />
      ): null}

      {clubId && inVerifying && isAgisMembershipMode ? (
        <MembershipValidationModal
          show={inVerifying}
          clubId={clubId}
          onClose={onMembershipValidationModalClose}
          onVerified={onMembershipValidationSuccess}
        />
      ) : null}
    </>
  );
};

GoodsamMembership.propTypes = {
  activeId: PropTypes.string,
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  setIsGoodSamMember: PropTypes.func.isRequired,
  setIsDeclined: PropTypes.func.isRequired,

  /**
   * Will replace modal cross with button to go back on other modal
   */
  // backTo={{ label: "Back to booking", onClick: () => null }}
  backTo: PropTypes.exact({
    onClick: PropTypes.func,
    label: PropTypes.string.isRequired
  }),
  onSavePlan: PropTypes.func.isRequired
};

export default GoodsamMembership;
